import React, { useEffect, useRef, useState } from 'react';
import { FileConfig } from '~/constants/fileUpload';
import { filterOutIncorrectFiles } from '~/helpers/files.helper';
import { validateFiles } from '~/helpers/validators.helper';
import { IFile } from '~/interfaces/shared';
import DocumentErrorMessage, { FileUploadErrors } from './DocumentErrorMessage';
import DocumentList from './DocumentList';
import DocumentUpload from './DocumentUpload';

interface Props {
  fileConfig: FileConfig;
  hasApiError: boolean;
  onFilesChange: (files: IFile[]) => void;
}

export const DocumentUploadSection = (props: Props) => {
  const { fileConfig, hasApiError, onFilesChange } = props;

  const {
    maxSize,
    maxFiles,
    allowedFileExtensionsDisplayed,
    allowedFileFormats,
  } = fileConfig;

  const allowedFileFormatsAndExtensions = [
    ...allowedFileFormats,
    ...allowedFileExtensionsDisplayed,
  ];

  const [localFiles, setLocalFiles] = useState<IFile[]>([]);
  const [
    localFileUploadErrors,
    setLocalFileUploadErrors,
  ] = useState<FileUploadErrors>({
    alreadyAdded: false,
    invalidType: false,
    tooLarge: false,
    maxLimit: false,
  });
  const [
    lastLocalUploadHadMultipleFiles,
    setLastLocalUploadHadMultipleFiles,
  ] = useState(false);
  const fileListRef = useRef<HTMLDivElement>(null);
  const localFilesRef = useRef<IFile[]>([]);
  localFilesRef.current = localFiles;

  useEffect(() => {
    return () => {
      localFilesRef.current.forEach((localFile) => {
        URL.revokeObjectURL(localFile.objectURL);
      });
    };
  }, []);

  const setErrors = (filesToCheck: IFile[]) => {
    const { hasAnyFormatIssue, hasAnySizeIssue } = validateFiles(
      filesToCheck,
      allowedFileFormatsAndExtensions,
      maxSize
    );

    const fileIsAlreadyAdded = filesToCheck.some(
      (file) => filesToCheck.filter((f) => f.name === file.name).length > 1
    );

    setLocalFileUploadErrors({
      alreadyAdded: fileIsAlreadyAdded,
      maxLimit: filesToCheck.length > maxFiles,
      invalidType: hasAnyFormatIssue,
      tooLarge: hasAnySizeIssue,
    });
  };
  const onLocalUploadFiles = (acceptedFiles: File[]) => {
    setLastLocalUploadHadMultipleFiles(acceptedFiles.length > 1);

    const mappedFiles = acceptedFiles.map((file) => ({
      name: file.name,
      size: file.size,
      type: file.type,
      timestamp: file.lastModified,
      objectURL: URL.createObjectURL(file),
    }));

    const nextFiles = filterOutIncorrectFiles(
      mappedFiles.concat(localFiles),
      allowedFileFormatsAndExtensions,
      maxSize,
      maxFiles
    );

    setErrors(localFiles.concat(mappedFiles));
    setLocalFiles(nextFiles);
    onFilesChange(nextFiles);

    fileListRef.current?.scrollIntoView();
  };

  const handleRemoveFile = (file: IFile) => {
    URL.revokeObjectURL(file.objectURL);
    const removedObjArray = localFiles.filter(
      (localFile) => localFile.objectURL !== file.objectURL
    );
    setLocalFiles(removedObjArray);
    setErrors(removedObjArray);
    onFilesChange(removedObjArray);
  };

  const onDeleteAll = () => {
    localFiles.forEach((localFile) => {
      URL.revokeObjectURL(localFile.objectURL);
    });
    setLocalFiles([]);
    setErrors([]);
    onFilesChange([]);
  };

  const maxFilesUploaded = localFiles.length >= maxFiles;

  return (
    <>
      {!maxFilesUploaded && (
        <DocumentUpload
          allowedFileExtensionsDisplayed={allowedFileExtensionsDisplayed}
          maxFiles={maxFiles}
          maxSize={maxSize}
          onUploadFiles={onLocalUploadFiles}
        />
      )}

      <DocumentErrorMessage
        allowedFileExtensionsDisplayed={allowedFileExtensionsDisplayed}
        fileUploadErrors={localFileUploadErrors}
        hasApiError={hasApiError}
        lastLocalUploadHadMultipleFiles={lastLocalUploadHadMultipleFiles}
        maxFiles={maxFiles}
        maxSize={maxSize}
      />

      {!!localFiles.length && (
        <DocumentList
          fileListRef={fileListRef}
          files={localFiles}
          handleRemoveFile={handleRemoveFile}
          onDeleteAll={onDeleteAll}
        />
      )}
    </>
  );
};
